/**
 * @file apps/web/src/pages/UsersSettingsPage.tsx
 * @description Team management settings page.
 * Lists all team members for the tenant, supports role updates, and inviting new members (stub).
 * Route: /settings/users
 */

import { useEffect, useState } from 'react';
import { AlertCircle, Plus, Mail, Shield, User, Trash2, RefreshCw } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { workspacesApi } from '../lib/api.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TeamMember {
  id:         string;
  full_name?: string | null;
  email:      string;
  role?:      string | null;
}

// Must match packages/types/src/enums.ts's TenantRole exactly — this list is
// sent straight through to POST /tenants/:id/members, which rejects anything
// outside the tenant_role DB enum.
const ROLES = ['owner', 'admin', 'consultant', 'viewer'] as const;
type Role = typeof ROLES[number];

const ROLE_LABELS: Record<Role, string> = {
  owner:      'Owner',
  admin:      'Admin',
  consultant: 'Consultant',
  viewer:     'Viewer',
};

const ROLE_COLORS: Record<Role, string> = {
  owner:      'bg-blue/10 text-blue',
  admin:      'bg-success/10 text-success',
  consultant: 'bg-sky text-ink',
  viewer:     'bg-gray text-muted',
};

// ---------------------------------------------------------------------------
// Member row
// ---------------------------------------------------------------------------

function MemberRow({
  member,
  isCurrentUser,
  onRemove,
}: {
  member:        TeamMember;
  isCurrentUser: boolean;
  onRemove:      (id: string) => void;
}) {
  const role = (member.role ?? 'viewer') as Role;
  const initials = (member.full_name ?? member.email)
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <tr className="table-row group">
      <td className="table-cell">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[11px] font-mono font-bold shrink-0"
            style={{ backgroundColor: 'var(--blue)' }}
          >
            {initials}
          </div>
          <div>
            <p className="text-sm font-medium text-ink">
              {member.full_name ?? '—'}
              {isCurrentUser && (
                <span className="ml-1.5 text-[10px] font-mono text-muted">(you)</span>
              )}
            </p>
            <p className="text-[11px] font-mono text-muted">{member.email}</p>
          </div>
        </div>
      </td>
      <td className="table-cell">
        <span className={[
          'text-[11px] font-mono px-2 py-0.5 rounded-full',
          ROLE_COLORS[role] ?? 'bg-gray text-muted',
        ].join(' ')}>
          {ROLE_LABELS[role] ?? role}
        </span>
      </td>
      <td className="table-cell text-right">
        {!isCurrentUser && (
          <button
            onClick={() => onRemove(member.id)}
            className="btn-icon w-7 h-7 text-muted opacity-0 group-hover:opacity-100 transition-opacity hover:text-danger"
            title="Remove member"
          >
            <Trash2 size={13} />
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Invite form
// ---------------------------------------------------------------------------

function InviteForm({
  onCancel,
  onInvite,
}: {
  onCancel:  () => void;
  onInvite:  (email: string, role: Role) => Promise<void>;
}) {
  const [email, setEmail]   = useState('');
  const [role, setRole]     = useState<Role>('consultant');
  const [loading, setLoading] = useState(false);
  const [err, setErr]       = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setErr('Email is required.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErr('Enter a valid email.'); return; }
    setLoading(true);
    setErr('');
    try {
      await onInvite(email.trim(), role);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-ink">Invite Team Member</h3>
        <button onClick={onCancel} className="btn-ghost text-xs text-muted">Cancel</button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <div>
            <label className="label mb-1">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@yourfirm.com"
              className="input text-sm"
              autoFocus
            />
          </div>
          <div>
            <label className="label mb-1">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className="select text-sm"
            >
              {ROLES.map((r) => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
        </div>

        {err && (
          <div className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle size={13} /> {err}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary text-sm">
            <Mail size={14} />
            {loading ? 'Sending…' : 'Send Invite'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// UsersSettingsPage
// ---------------------------------------------------------------------------

export function UsersSettingsPage() {
  const { user, devHeaders } = useAuth();

  const [members, setMembers]   = useState<TeamMember[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [notice, setNotice]     = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    workspacesApi.members(user.tenantId, devHeaders)
      .then((data) => {
        if (!cancelled) setMembers(data as TeamMember[]);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load team members');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInvite = async (email: string, role: Role) => {
    const created = await workspacesApi.invite(user.tenantId, { email, role }, devHeaders) as TeamMember;
    setMembers((prev) => [...prev, created]);
    setShowInvite(false);
    setNotice(`Invite sent to ${email}. They'll receive an email shortly.`);
    setTimeout(() => setNotice(''), 5000);
  };

  const handleRemove = async (id: string) => {
    const member = members.find((m) => m.id === id);
    if (!member) return;
    const name = member.full_name ?? member.email;
    if (!confirm(`Remove ${name} from the team? This cannot be undone.`)) return;
    try {
      await workspacesApi.removeMember(user.tenantId, id, devHeaders);
      setMembers((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove team member');
    }
  };

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Team Members</h1>
          {!loading && (
            <p className="text-sm text-muted mt-1">
              {members.length} member{members.length !== 1 ? 's' : ''} · Clarity Consulting
            </p>
          )}
        </div>
        {!showInvite && (
          <button onClick={() => setShowInvite(true)} className="btn-primary text-sm">
            <Plus size={14} /> Invite Member
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {notice && (
        <div className="flex items-center gap-2 p-3 bg-success/8 rounded-lg text-success text-sm mb-4">
          <Mail size={14} className="shrink-0" /> {notice}
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <div className="mb-4">
          <InviteForm
            onCancel={() => setShowInvite(false)}
            onInvite={handleInvite}
          />
        </div>
      )}

      {/* Role legend */}
      <div className="card p-3 mb-4 flex items-start gap-3">
        <Shield size={14} className="text-muted shrink-0 mt-0.5" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-[11px]">
          {ROLES.map((r) => (
            <div key={r} className="flex items-center gap-1.5">
              <span className={['px-1.5 py-0.5 rounded-full font-mono', ROLE_COLORS[r]].join(' ')}>
                {ROLE_LABELS[r]}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Member table */}
      {loading ? (
        <div className="card animate-pulse space-y-px">
          {[1,2,3,4].map((i) => (
            <div key={i} className="h-14 bg-gray/40" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="empty-state py-12">
          <User size={24} className="text-muted/40 mb-2 mx-auto" />
          <p className="text-sm text-muted">No team members found.</p>
          <button onClick={() => setShowInvite(true)} className="btn-secondary mt-4">
            <Plus size={14} /> Invite First Member
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="table-header">Member</th>
                <th className="table-header w-32">Role</th>
                <th className="table-header w-12" />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  isCurrentUser={m.id === user.userId}
                  onRemove={handleRemove}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Stub notice */}
      <div className="flex items-start gap-2 mt-4 p-3 bg-sky rounded-lg">
        <RefreshCw size={13} className="text-blue shrink-0 mt-0.5" />
        <p className="text-[11px] font-mono text-blue/80">
          Invites and removals persist to the workspace for real. The invite
          email itself is logged, not actually delivered — Resend is stubbed.
        </p>
      </div>
    </div>
  );
}
